import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { AxiosResponse } from "axios";
import { UploadOutlined } from "@ant-design/icons";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Button, Form, Input, Upload, UploadFile } from "antd";
import { UploadChangeParam } from "antd/lib/upload";
import { useFiles } from "../../../../hooks/use-files";
import { setSelectedMenuItemKeys } from "../../../../../../redux/reducer/antd-layout-info.reducer";
import { showLoadingAlertMessage } from "../../../../../../utils/show-loading-alert-message/show-loading-alert-message";
import { networkCall } from "../../../../../../utils/network-call/network-call";
import { FileType } from "../../../../types/api/get-file-with-siblings-success-response";
import { showDefaultErrorMessages } from "../../../../../../utils/show-default-error-message/show-default-error-messages";
import { AlertMessageState, clearAlertMessage, showAlertMessage } from "../../../../../../redux/reducer/alert-message.reducer";
import { AlertMessages } from "../../../../../../enums/alert-messages.enum";
import { CreateFilesSuccessResponse } from "../../../../types/api/create-files-success-response";
import { DefaultErrorResponse } from "../../../../../../types/api/error/default-error-response/default-error-respose.type";
import { ValidationErrorResponseData } from "../../../../../../types/api/error/validation-error-response-data/validation-error-response-data.type.";
import { tranformAPIValidationErrorToAntDValidationMessage } from "../../../../../../utils/validation/transform-api-validation-errors-to-antd-validation-messsages";
import { SearchBar } from "../../../../../../components/search-bar/search-bar";
import { CMSButton } from "../../../../../../components/cms/button/cms-button.";
import { getImageUrl } from "../../../../../../utils/image-helper/get-image-url";
import { FileList } from "../../../../components/file-list/file-list";

export const CompanyAdminFileList = () => {
    const dispatch = useDispatch();
    const [isNewFolderModalOpen, setIsNewFolderModalOpen] = useState(false);
    const [newFolderForm] = Form.useForm();
    const [isNewFilesModalOpen, setIsNewFilesModalOpen] = useState(false);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [isUploadFolderModalOpen, setIsUploadFolderModalOpen] = useState(false);
    const [folderUploadFileList, setFolderUploadFileList] = useState<UploadFile[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const searchTermRef = useRef('');
    const [ignoreParent, setIgnoreParent] = useState(false);
    const {
        files,
        parentFolderId,
        goBackByOneFolder,
        openFolder,
        addFiles,
        deleteFile,
        searchFiles,
        renameFolder,
        moveFile
    } = useFiles();

    useEffect(() => {
        dispatch(setSelectedMenuItemKeys(['company-admin-files']));
    }, [dispatch]);

    const handleDeleteFile = (id: number) => {
        deleteFile(id);
    };

    const handleFolderOpen = async (id: number) => {
        const isOpened = await openFolder(id);

        if (isOpened) {
            setIgnoreParent(false);
            setSearchTerm('');
            searchTermRef.current = '';
        }
    };

    const handleNewFolderFormFinish = async (values: any) => {
        showLoadingAlertMessage();

        const response = await networkCall('/files', {
            method: 'POST',
            body: {
                name: values.name,
                parentId: parentFolderId === null ? 0 : parentFolderId,
                type: FileType.Directory
            }
        });

        const messageShowed = showDefaultErrorMessages(response);
        if (messageShowed) return;

        dispatch(clearAlertMessage());
        const alertMessageState: AlertMessageState = {
            alertMessageType: 'error',
            alertMessageText: AlertMessages.UnknownErrorTryAgain
        };

        if (response?.status === 201) {
            const res: AxiosResponse<CreateFilesSuccessResponse> = response;
            alertMessageState.alertMessageText = 'Mentés sikeres.';
            alertMessageState.alertMessageType = 'success';
            newFolderForm.resetFields();
            addFiles(res.data.files);
            setIsNewFolderModalOpen(false);

        } else if (response?.status === 400) {
            const res: AxiosResponse<DefaultErrorResponse> = response;

            switch (res.data.code) {

                case 1000:
                    const _res: AxiosResponse<DefaultErrorResponse<ValidationErrorResponseData[]>> = response;
                    alertMessageState.alertMessageText = 'Hibásan megadott beviteli mezők.';
                    const validationErrors = tranformAPIValidationErrorToAntDValidationMessage(_res.data.data!);
                    newFolderForm.setFields(validationErrors);
                    break;

            }

        }

        dispatch(showAlertMessage(alertMessageState));
    };

    const handleFilesUploadClick = async () => {
        if (!fileList || fileList.length === 0) return;

        showLoadingAlertMessage();

        const response = await networkCall('/files', {
            method: 'POST',
            requestBodyType: 'FILE_FORM',
            body: {
                files: fileList.map(file => file.originFileObj),
                parentId: parentFolderId === null ? 0 : parentFolderId,
                type: FileType.SimpleFile
            }
        });

        const messageShowed = showDefaultErrorMessages(response);
        if (messageShowed) return;

        dispatch(clearAlertMessage());
        const alertMessageState: AlertMessageState = {
            alertMessageType: 'error',
            alertMessageText: AlertMessages.UnknownErrorTryAgain
        };

        if (response?.status === 201) {
            const res: AxiosResponse<CreateFilesSuccessResponse> = response;
            alertMessageState.alertMessageText = 'Mentés sikeres.';
            alertMessageState.alertMessageType = 'success';
            setFileList([]);

            addFiles(res.data.files);
            setIsNewFilesModalOpen(false);
        }

        dispatch(showAlertMessage(alertMessageState));
    };

    const handleFolderUploadClick = async () => {
        if (!folderUploadFileList || folderUploadFileList.length ===0) return;

        showLoadingAlertMessage();

        const response = await networkCall('/files', {
            method: 'POST',
            requestBodyType: 'FILE_FORM',
            body: {
                files: folderUploadFileList.map(file => file.originFileObj),
                parentId: parentFolderId === null ? 0 : parentFolderId,
                isFile: FileType.SimpleFile
            }
        });

        const messageShowed = showDefaultErrorMessages(response);
        if (messageShowed) return;

        dispatch(clearAlertMessage());
        const alertMessageState: AlertMessageState = {
            alertMessageType: 'error',
            alertMessageText: AlertMessages.UnknownErrorTryAgain
        };

        if (response?.status === 201) {
            const res: AxiosResponse<CreateFilesSuccessResponse> = response;
            alertMessageState.alertMessageText = 'Mentés sikeres.';
            alertMessageState.alertMessageType = 'success';
            setFolderUploadFileList([]);

            addFiles(res.data.files);
            setIsUploadFolderModalOpen(false);
        }

        dispatch(showAlertMessage(alertMessageState));
    };

    const handleFilesUploaderChange = (info: UploadChangeParam) => {
        if (info.fileList) setFileList(info.fileList);
    };

    const handleFolderUploaderChange = (info: UploadChangeParam) => {
        if (info.fileList) setFolderUploadFileList(info.fileList);
    };

    const handleSearchBarChange = async (value: string) => {
        setSearchTerm(value);
        searchFiles(value);
        setIgnoreParent(value !== '');
    };

    const handleFileMove = async (id: number) => {
        // ha abba a könyvtárba helyezzük, ahol jelenleg is van
        if (files.some(file => file.id === id)) return;

        moveFile(id, parentFolderId);
    };

    return (
        <>
            <div className="page-title">Fájlok</div>
            <div className="d-flex mb-4 justify-content-between gap-3">
                <SearchBar value={searchTerm} onChange={handleSearchBarChange} placeholder="Keresés az összes fájl és mappa között" />
                <div className="d-flex gap-3">
                    <CMSButton text="Új mappa" type="purple" iconSrc={getImageUrl('folder-add-icon.png')} onClick={() => setIsNewFolderModalOpen(true)} />
                    <CMSButton text="Fájl(ok) feltöltése" iconSrc={getImageUrl('add-file-icon.png')} onClick={() => setIsNewFilesModalOpen(true)} />
                    <CMSButton text="Mappa feltöltése" iconSrc={getImageUrl('folder-upload-icon.png')} onClick={() => setIsUploadFolderModalOpen(true)} />
                </div>
            </div>
            <FileList
                files={files}
                type="fileViewer"
                hasParentFolder={parentFolderId !== null && !ignoreParent}
                onFolderOpen={handleFolderOpen}
                onDelete={handleDeleteFile}
                onGoBackByOneFolder={goBackByOneFolder}
                onFolderRename={renameFolder}
                onFileMove={handleFileMove}
            />
            {/* Add folder modal */}
            <Modal isOpen={isNewFolderModalOpen} toggle={() => setIsNewFolderModalOpen(!isNewFolderModalOpen)}>
                <ModalHeader>Új mappa</ModalHeader>
                <ModalBody>
                    <Form
                        form={newFolderForm}
                        onFinish={handleNewFolderFormFinish}
                        layout='vertical'
                    >
                        <Form.Item
                            label={<b>Név</b>}
                            name='name'
                            rules={[{required: true, message: 'Név megadása kötelező!'}]}
                        >
                            <Input className='box-shadow-4-4-8-0 border-0' />
                        </Form.Item>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <CMSButton text="Mégse" type="white" onClick={() => setIsNewFolderModalOpen(false)} />
                    <CMSButton text="Mentés" iconSrc={getImageUrl('Save-icon.png')} onClick={() => newFolderForm.submit()} />
                </ModalFooter>
            </Modal>
            {/* Add file(s) modal */}
            <Modal isOpen={isNewFilesModalOpen} toggle={() => setIsNewFilesModalOpen(!isNewFilesModalOpen)}>
                <ModalHeader>Fájl(ok) feltöltése</ModalHeader>
                <ModalBody>
                    <Upload
                        fileList={fileList}
                        beforeUpload={() => false}
                        onChange={handleFilesUploaderChange}
                        multiple
                    >
                        <Button icon={<UploadOutlined />}>Feltöltéshez kattints ide!</Button>
                    </Upload>
                </ModalBody>
                <ModalFooter>
                    <CMSButton text="Mégse" type="white" onClick={() => setIsNewFilesModalOpen(false)} />
                    <CMSButton text="Mentés" iconSrc={getImageUrl('Save-icon.png')} onClick={handleFilesUploadClick} />
                </ModalFooter>
            </Modal>
            {/* Add whole directory(containing files) modal */}
            <Modal isOpen={isUploadFolderModalOpen} toggle={() => setIsUploadFolderModalOpen(!isUploadFolderModalOpen)}>
                <ModalHeader>Mappa feltöltése</ModalHeader>
                <ModalBody>
                    <Upload
                        onChange={handleFolderUploaderChange}
                        beforeUpload={() => false}
                        directory
                        fileList={folderUploadFileList}
                    >
                        <Button icon={<UploadOutlined />}>Feltöltéshez kattints ide!</Button>
                    </Upload>
                </ModalBody>
                <ModalFooter>
                    <CMSButton text="Mégse" type="white" onClick={() => setIsUploadFolderModalOpen(false)} />
                    <CMSButton text="Mentés" iconSrc={getImageUrl('Save-icon.png')} onClick={handleFolderUploadClick} />
                </ModalFooter>
            </Modal>
        </>
    );
};